import React, {
  FunctionComponent,
  useCallback,
  useState,
  useMemo,
} from 'react';
import moment from 'moment';
import { orderBy } from 'lodash';
import { MdOpenInNew } from 'react-icons/md';
import { Dealer } from '../../../../../types/dealerTypes';
import { TableTitle } from '../../../../TableTitle/TableTitle';
import { LinkButton } from '../../../../Input/Button/LinkButton';
import { ROUTES } from '../../../../../utils/routes';
import { ACTION_ITEMS } from '../../../actionItems';
import styles from './styles.module.scss';

type Props = {
  dealers: Array<Dealer>;
};

const STATUS = {
  NOT_STARTED: {
    label: 'Not started',
    color: '#CCC',
  },
  LAUNCHED: {
    label: 'Launched',
    color: '#0765F9',
  },
  NEEDS_REVIEW: {
    label: 'Needs Review',
    color: '#EE1A30',
  },
  READY_FOR_LAUNCH: {
    label: 'Ready For Launch',
    color: '#0CCA58',
  },
  IN_PROGRESS: {
    label: 'In Progress',
    color: '#8F07F9',
  },
  DELAYED: {
    label: 'Delayed',
    color: '#F97B07',
  },
};

const daysConsideredDelayed = 3;

export const SupportDealerList: FunctionComponent<Props> = ({ dealers }) => {
  const [sortByAsc, setSortByAsc] = useState(false);
  const [sorter, setSorter] = useState('createdAt');

  const renderStatus = useCallback((dealer: Dealer) => {
    // Has dealer logged in within 3 days, if not, they are delayed
    const delayedDealerDate = moment().subtract(daysConsideredDelayed, 'd');
    if (!dealer.launchpad?.firstLogin) {
      return STATUS.NOT_STARTED;
    } else if (dealer.liveAt) {
      return STATUS.LAUNCHED;
    } else if (ACTION_ITEMS.find((item) => item.isActive(dealer))) {
      return STATUS.NEEDS_REVIEW;
    } else if (dealer.launchpad.status?.readyToLaunch) {
      return STATUS.READY_FOR_LAUNCH;
    } else if (moment(dealer.launchpad.lastLogin) > delayedDealerDate) {
      return STATUS.IN_PROGRESS;
    }
    return STATUS.DELAYED;
  }, []);

  const sortedDealers = useMemo(() => {
    if (sorter === 'obtainStatus') {
      return orderBy(
        dealers,
        (dealer: Dealer) => renderStatus(dealer)?.label,
        sortByAsc ? 'asc' : 'desc',
      );
    }
    return orderBy(dealers, sorter, sortByAsc ? 'asc' : 'desc');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sorter, sortByAsc, dealers]);

  const sort = useCallback(
    (newSorter) => () => {
      // If the sorter is the same, change the order
      if (sorter === newSorter) {
        setSortByAsc(!sortByAsc);
      } else {
        setSorter(newSorter);
      }
    },
    [sorter, sortByAsc],
  );

  function handleClick(e: any, dealerId: string) {
    e.preventDefault();
    const dealerUrl = `/admin/${dealerId}`;
    // open a new tab for the dealer
    window.open(dealerUrl, '_blank');
  }

  return (
    <div className={styles.supportDealerListContainer}>
      <h1>Client Progress</h1>
      <div className={styles.supportDealerListHeader}>
        <TableTitle title="Dealer" sortable onClick={sort('name')} />
        <TableTitle title="Email" sortable onClick={sort('launchpad.email')} />
        <TableTitle title="Created" sortable onClick={sort('createdAt')} />
        <TableTitle
          title="First Login"
          sortable
          onClick={sort('launchpad.firstLogin')}
        />
        <TableTitle title="Status" sortable onClick={sort('obtainStatus')} />
        <TableTitle title="" />
      </div>
      {sortedDealers.map((dealer) => {
        const email = dealer.launchpad?.email || '';
        const firstLogin = dealer.launchpad?.firstLogin || '';
        const { label: statusLabel, color: statusColor } = renderStatus(dealer);
        return (
          <LinkButton
            key={dealer.id}
            className={styles.supportDealerListRow}
            to={ROUTES.SUPPORT_DASHBOARD_VIEW_DEALER.getPath(dealer.id)}
            variant="minimal"
          >
            <div>{dealer.name}</div>
            <div>{email}</div>
            <div>
              {dealer.createdAt
                ? moment(dealer.createdAt).format('MM/DD/YYYY')
                : 'N/A'}
            </div>
            <div>
              {firstLogin ? moment(firstLogin).format('MM/DD/YYYY') : 'N/A'}
            </div>
            <div className={styles.supportDealerListStatus}>
              {statusColor && (
                <div
                  className={styles.statusDot}
                  style={{ backgroundColor: statusColor || '' }}
                />
              )}
              {statusLabel}
            </div>
            <button
              type="button"
              className={styles.supportDealerListOpenLaunchpad}
              onClick={(e) => handleClick(e, dealer.id)}
            >
              <MdOpenInNew className={styles.launchpadIcon} />
              <span className={styles.launchpadText}>Open Launch Pad</span>
            </button>
          </LinkButton>
        );
      })}
    </div>
  );
};
